import React, { useMemo, useState, useRef, useEffect } from "react";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutlined";
import { GetStorageProfileResponse } from "target_wasm";
import { SidebarItems } from "./SidebarItems";
import type { SidebarItem } from "./SidebarItems";
import { SidebarDropdown } from "./SidebarDropdown";
import type { DropdownItem } from "./SidebarDropdown";
import { QuotaProgressBar } from "./QuotaProgressBar";

interface StorageIndicatorProps {
  isOpen: boolean;
  storageProfile: GetStorageProfileResponse | null;
  fetchState: "loading" | "loaded" | "error";
  onSupportClick: (anchorTop?: number) => void;
  sidebarWidth: string;
}

export const StorageIndicator: React.FC<StorageIndicatorProps> = ({
  isOpen,
  storageProfile,
  fetchState,
  onSupportClick,
  sidebarWidth,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showDropdown) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        wrapperRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setShowDropdown(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [showDropdown]);

  const percentage = useMemo(() => {
    if (!storageProfile) return 0;
    return (
      (Number(storageProfile.usage_bytes) /
        Number(storageProfile.quota_bytes)) *
      100
    );
  }, [storageProfile]);

  const iconColor = useMemo(() => {
    if (fetchState === "error" && !storageProfile) return "var(--color-error)";
    if (!storageProfile) return undefined;
    if (percentage >= 90) return "var(--color-error)";
    if (percentage >= 70) return "var(--color-warning)";
    return undefined;
  }, [storageProfile, fetchState, percentage]);

  const usageText = storageProfile
    ? `${storageProfile.usage_human_readable} / ${storageProfile.quota_human_readable}`
    : undefined;

  const tooltipText = useMemo(() => {
    if (fetchState === "loading" && !storageProfile) return "Loading...";
    if (fetchState === "error" && !storageProfile)
      return "Could not load quota";
    if (storageProfile) {
      return `${usageText} (${Math.round(percentage)}%)`;
    }
    return "Quota";
  }, [fetchState, storageProfile, usageText, percentage]);

  const secondaryText = useMemo(() => {
    if (fetchState === "error" && !storageProfile) return "Unavailable";
    return undefined;
  }, [fetchState, storageProfile]);

  const customContent = useMemo(() => {
    if (fetchState === "loading" && !storageProfile) {
      return (
        <div
          className="w-full h-0.75 rounded-app overflow-hidden"
          style={{ background: "var(--color-bg-subtle)" }}
        >
          <div
            className="h-full rounded-app animate-pulse"
            style={{
              width: "60%",
              background: "var(--color-bg-subtle)",
            }}
          />
        </div>
      );
    }
    if (storageProfile) {
      return (
        <div className="w-full flex flex-col gap-1">
          <span
            className="text-fg-muted-regular-small truncate"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            {usageText}
          </span>
          <QuotaProgressBar percentage={percentage} />
        </div>
      );
    }
    return undefined;
  }, [fetchState, storageProfile, percentage, usageText]);

  const handleClick = () => {
    if (!storageProfile) return;
    if (buttonRef.current) {
      setDropdownTop(buttonRef.current.getBoundingClientRect().top);
    }
    setShowDropdown((prev) => !prev);
  };

  const handleDropdownHoverChange = (hovered: boolean) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    if (!hovered) {
      dropdownTimeoutRef.current = setTimeout(
        () => setShowDropdown(false),
        150,
      );
    }
  };

  const statusMessage = useMemo(() => {
    if (percentage >= 90) return "Storage almost full";
    if (percentage >= 70) return "Storage running low";
    return undefined;
  }, [percentage]);

  const headerContent = storageProfile ? (
    <div className="px-3 py-2.5 border-b border-border">
      <div className="text-mono-small mb-2">
        {storageProfile.usage_human_readable} of{" "}
        {storageProfile.quota_human_readable} used ({Math.round(percentage)}%)
      </div>
      <div className="mb-2">
        <QuotaProgressBar percentage={percentage} size="md" />
      </div>
      <div className="flex items-center gap-2">
        <span
          className="inline-block px-2 py-px rounded-full text-fg-muted-medium-xs"
          style={{ background: "var(--color-bg-hover)" }}
        >
          Alpha
        </span>
        {statusMessage && (
          <span
            style={{
              fontSize: "var(--font-size-x-small)",
              color:
                percentage >= 90
                  ? "var(--color-error)"
                  : "var(--color-warning)",
            }}
          >
            {statusMessage}
          </span>
        )}
      </div>
    </div>
  ) : undefined;

  const dropdownItems: DropdownItem[] = [
    {
      id: "support",
      label: "Contact Support",
      icon: <HelpOutlineIcon className="icon-xs" />,
      onClick: () => {
        setShowDropdown(false);
        onSupportClick(dropdownTop);
      },
    },
  ];

  const sidebarItem: SidebarItem = {
    icon: (
      <CloudOutlinedIcon
        className="icon-md cursor-default"
        style={{ color: iconColor }}
      />
    ),
    text: "Quota",
    secondaryText,
    tooltip: tooltipText,
    customContent,
    onClick: handleClick,
    buttonRef,
  };

  return (
    <>
      <SidebarItems
        items={[sidebarItem]}
        isOpen={isOpen}
        variant="profile"
        sidebarWidth={sidebarWidth}
      />
      <div
        ref={wrapperRef}
        className="contents"
        onMouseEnter={() => {
          if (dropdownTimeoutRef.current)
            clearTimeout(dropdownTimeoutRef.current);
        }}
        onMouseLeave={() => setShowDropdown(false)}
      >
        <SidebarDropdown
          items={dropdownItems}
          isVisible={showDropdown}
          topPosition={dropdownTop}
          sidebarWidth={sidebarWidth}
          headerContent={headerContent}
          onHoverChange={handleDropdownHoverChange}
        />
      </div>
    </>
  );
};
